import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { AlertTriangle, ArrowLeft, Building2, Check, ChevronDown, ExternalLink, FileX2, Loader2, LogOut, MoreHorizontal, Pencil, PlugZap, RefreshCw, Trash2, Unplug, X } from 'lucide-react'
import type {
  QuickBooksCfoCoreBootstrapResponse,
  QuickBooksConnectionStatus,
  QuickBooksStatusResponse,
  ServerApiClient,
  ServerSiteSummary,
  ServerWorkbookDeleteImpact,
} from '../../../data/ServerApiClient'
import { WorkbookDeleteDialog } from '../WorkbookDeleteDialog'
import { isDemoSession } from '../../../data/demoSession'
import { loadCfoBootstrapCached } from './useCfoBootstrap'
import { navigateToSiteHome } from './CfoPageShell'
import { trimStr } from './cfoFormat'
import { parseCfoRefusal } from './cfoRefusalCopy'
import { buildCfoRoutePath, buildFirmCockpitPath, parseCfoRouteState } from './cfoRoutes'
import { useCfoFirmNavigationOptional } from './CfoFirmNavigation'

type CfoCoreFileMenuProps = {
  appApi: ServerApiClient
  workbookId: string | null
  accountingProvider?: string | null
  triggerLabel?: string
  /**
   * When true, the trigger shows the current company name and the menu gains a
   * "Switch company" section listing every CFO Core file the user can access.
   * Used in the CFO shell topbar; left off for the per-row "Manage" menu in the
   * file launcher.
   */
  showSwitcher?: boolean
  onDeleted?: () => void | Promise<void>
  onRenamed?: () => void | Promise<void>
  onConnectionChanged?: () => void | Promise<void>
}

type CfoSwitcherFile = {
  workbookId: string
  siteSlug: string
  siteName: string
  label: string
  current: boolean
}

type SwitcherStatus = 'idle' | 'loading' | 'ready' | 'error'

/** CFO Core workbooks are named "{Company} CFO Core" at creation; strip the
 * suffix so the switcher reads as a plain company name. Falls back to the raw
 * workbook name if the convention does not match. */
const companyLabelFromWorkbookName = (name: string | null | undefined): string => {
  const trimmed = trimStr(name)
  if (!trimmed) return ''
  return trimmed.replace(/\s+CFO Core\s*$/i, '').trim() || trimmed
}

const formatError = (error: any, fallback: string): string => {
  const raw = trimStr(error?.message || error?.body || error || fallback)
  if (/pool timed out while waiting for an open connection/i.test(raw)) {
    return 'The backend database connection timed out. Try again in a moment.'
  }
  if (/failed to fetch/i.test(raw)) {
    return 'FuseGrid could not reach the backend.'
  }
  return raw || fallback
}

/** The `error` code and connection `status` of a JSON API error body, when it
 * has them. The body's `message` is the operator text — what the reader sees
 * comes from `parseCfoRefusal`. */
const apiErrorBody = (error: any): { code: string; status: string } => {
  try {
    const parsed = JSON.parse(trimStr(error?.body))
    return { code: trimStr(parsed?.error), status: trimStr(parsed?.status) }
  } catch {
    return { code: '', status: '' }
  }
}

// Refresh refusals share statuses (409 for a refresh already running, a
// company whose ledger index is not built yet, a demo company, or an
// accounting connection that cannot refresh), so only the error code tells
// them apart. The refresh button is enabled only for an active connection, so
// a connection refusal means the panel's status went stale; each is matched
// to the refusal its route actually sends.
const RECONNECT_CONNECTION_STATUSES = new Set(['needs_reconnect', 'revoked'])

const currentLocation = (): string | null => {
  if (typeof window === 'undefined') return null
  return `${window.location.origin}${window.location.pathname}${window.location.search}${window.location.hash}`
}

const currentSiteSlug = (): string => {
  if (typeof window === 'undefined') return ''
  const route = parseCfoRouteState(window.location)
  if (route?.siteSlug) return route.siteSlug
  const parts = window.location.pathname.split('/').map((part) => part.trim()).filter(Boolean)
  if (parts[1] !== 'workbooks') return ''
  try {
    return trimStr(decodeURIComponent(parts[0] || ''))
  } catch {
    return trimStr(parts[0] || '')
  }
}

const resolveConnection = (
  bootstrap: QuickBooksCfoCoreBootstrapResponse | null,
  status: QuickBooksStatusResponse | null,
): QuickBooksConnectionStatus | null => {
  const connections = Array.isArray(status?.connections) ? status!.connections : []
  if (!connections.length) return null
  const connectionId = trimStr(bootstrap?.connection_id || bootstrap?.bindings?.[0]?.connection_id)
  if (connectionId) {
    const match = connections.find((entry) => trimStr(entry.connection_id) === connectionId)
    if (match) return match
  }
  const realmId = trimStr(bootstrap?.realm_id)
  if (realmId) {
    const match = connections.find((entry) => trimStr(entry.realm_id) === realmId)
    if (match) return match
  }
  return null
}

export function CfoCoreFileMenu({
  appApi,
  workbookId,
  accountingProvider,
  triggerLabel = 'File',
  showSwitcher = false,
  onDeleted,
  onRenamed,
  onConnectionChanged,
}: CfoCoreFileMenuProps) {
  const firmNavigation = useCfoFirmNavigationOptional()
  const providerKey = trimStr(accountingProvider) || 'quickbooks'
  const isQuickBooks = providerKey === 'quickbooks'
  const providerName = ({
    quickbooks: 'QuickBooks', xero: 'Xero', sage_intacct: 'Sage Intacct',
    sage_accounting: 'Sage Accounting', netsuite: 'NetSuite',
  } as Record<string, string>)[providerKey] || providerKey
  const [menuOpen, setMenuOpen] = useState(false)
  const [panelOpen, setPanelOpen] = useState(false)
  const [loading, setLoading] = useState(false)
  const [switcherFiles, setSwitcherFiles] = useState<CfoSwitcherFile[]>([])
  const [switcherStatus, setSwitcherStatus] = useState<SwitcherStatus>('idle')
  const [busyAction, setBusyAction] = useState<'disconnect' | 'reconnect' | 'refresh' | 'delete' | ''>('')
  const [error, setError] = useState('')
  const [notice, setNotice] = useState('')
  const [bootstrap, setBootstrap] = useState<QuickBooksCfoCoreBootstrapResponse | null>(null)
  const [status, setStatus] = useState<QuickBooksStatusResponse | null>(null)
  const [accountingBootstrap, setAccountingBootstrap] = useState<any | null>(null)
  const [accountingConnection, setAccountingConnection] = useState<any | null>(null)
  const [deleteOpen, setDeleteOpen] = useState(false)
  const [deleteImpact, setDeleteImpact] = useState<ServerWorkbookDeleteImpact | null>(null)
  const [deleteImpactLoading, setDeleteImpactLoading] = useState(false)
  const [deleteUnsharedDatasources, setDeleteUnsharedDatasources] = useState(false)
  const [deleteError, setDeleteError] = useState('')
  const menuRef = useRef<HTMLDivElement | null>(null)

  const wb = trimStr(workbookId)

  /**
   * The shared synthetic demo signs everyone into the same read-only user, so
   * the file-level actions (rename, QuickBooks management, delete) are neither
   * permitted nor meaningful there — a visitor renaming "Cedar & Oak" would be
   * editing every other visitor's demo. Hide that whole section and keep the
   * menu to what a demo visitor can actually do: switch company, go back to the
   * cockpit, or sign out.
   */
  const demoSession = useMemo(() => isDemoSession(), [])
  const showFileActions = !demoSession

  const loadDetails = useCallback(async () => {
    // Only the file-actions panel needs the QuickBooks connection; the switcher
    // trigger label comes from the cached bootstrap primed below.
    if (!wb || !showFileActions) return
    setLoading(true)
    setError('')
    try {
      if (isQuickBooks) {
        const [nextBootstrap, nextStatus] = await Promise.all([
          loadCfoBootstrapCached(appApi, wb),
          appApi.getQuickBooksStatus(),
        ])
        setBootstrap(nextBootstrap)
        setStatus(nextStatus)
      } else {
        const [nextBootstrap, nextConnection] = await Promise.all([
          appApi.getAccountingWorkbookBootstrap(wb),
          appApi.getAccountingWorkbookConnection(wb),
        ])
        setAccountingBootstrap(nextBootstrap)
        setAccountingConnection(nextConnection)
      }
    } catch (err: any) {
      setError(formatError(err, `Could not load ${providerName} connection details.`))
    } finally {
      setLoading(false)
    }
  }, [appApi, isQuickBooks, providerName, showFileActions, wb])

  useEffect(() => {
    if (!menuOpen && !panelOpen) return
    void loadDetails()
  }, [loadDetails, menuOpen, panelOpen])

  // In switcher mode the trigger shows the current company name, so prime the
  // bootstrap (cached) on mount rather than waiting for the menu to open.
  useEffect(() => {
    if (!showSwitcher || !wb) return
    let cancelled = false
    void (async () => {
      try {
        if (isQuickBooks) {
          const next = await loadCfoBootstrapCached(appApi, wb)
          if (!cancelled) setBootstrap(next)
        } else {
          const next = await appApi.getAccountingWorkbookBootstrap(wb)
          if (!cancelled) setAccountingBootstrap(next)
        }
      } catch {
        /* keep the placeholder label; details load when the menu opens */
      }
    })()
    return () => {
      cancelled = true
    }
  }, [appApi, isQuickBooks, showSwitcher, wb])

  const loadSwitcher = useCallback(async () => {
    if (firmNavigation) return
    setSwitcherStatus('loading')
    try {
      const sites = await appApi.listMySites()
      const slugs = (Array.isArray(sites) ? sites : [])
        .map((site) => trimStr(site?.slug))
        .filter((slug): slug is string => !!slug)
      const summaries = await Promise.all(
        slugs.map((slug) =>
          appApi
            .getSiteSummary(slug)
            .then((value) => ({ ok: true as const, value }))
            .catch(() => ({ ok: false as const, value: null as ServerSiteSummary | null })),
        ),
      )
      const files: CfoSwitcherFile[] = []
      for (const result of summaries) {
        if (!result.ok || !result.value) continue
        const siteSlug = trimStr(result.value.site?.slug)
        if (!siteSlug) continue
        const siteName = trimStr(result.value.site?.name) || siteSlug
        for (const workbook of result.value.workbooks || []) {
          if (!workbook?.has_quickbooks_connection) continue
          const id = trimStr(workbook.id)
          if (!id) continue
          files.push({
            workbookId: id,
            siteSlug,
            siteName,
            label: companyLabelFromWorkbookName(workbook.name) || 'CFO Core file',
            current: id === wb,
          })
        }
      }
      files.sort((a, b) =>
        a.siteName.localeCompare(b.siteName, undefined, { sensitivity: 'base' })
        || a.label.localeCompare(b.label, undefined, { sensitivity: 'base' }))
      setSwitcherFiles(files)
      setSwitcherStatus('ready')
    } catch {
      setSwitcherFiles([])
      setSwitcherStatus('error')
    }
  }, [appApi, firmNavigation, wb])

  // Company pages and the firm cockpit share the provider-neutral portfolio
  // context. Reuse it here so Xero, Sage, and future providers participate in
  // the same top-right switcher instead of falling through the old QuickBooks-
  // only workbook filter.
  useEffect(() => {
    if (!showSwitcher || !menuOpen || !firmNavigation) return
    if (firmNavigation.status === 'loading') {
      setSwitcherStatus('loading')
      return
    }
    if (firmNavigation.status === 'error') {
      setSwitcherFiles([])
      setSwitcherStatus('error')
      return
    }
    const files = firmNavigation.clients.map((client) => ({
      workbookId: client.workbook_id,
      siteSlug: client.site_slug || firmNavigation.siteSlug,
      siteName: firmNavigation.portfolio?.site_name || firmNavigation.siteSlug,
      label: client.company_name || client.client_name || 'CFO Core file',
      current: client.workbook_id === wb,
    }))
    files.sort((a, b) => a.label.localeCompare(b.label, undefined, { sensitivity: 'base' }))
    setSwitcherFiles(files)
    setSwitcherStatus('ready')
  }, [firmNavigation, menuOpen, showSwitcher, wb])

  useEffect(() => {
    if (!showSwitcher || !menuOpen) return
    if (firmNavigation) return
    if (switcherStatus === 'idle' || switcherStatus === 'error') void loadSwitcher()
  }, [firmNavigation, showSwitcher, menuOpen, switcherStatus, loadSwitcher])

  // Group the switcher by site so multi-site users see companies under their
  // site, matching the file launcher. Headers are omitted for single-site users.
  const switcherGroups = useMemo(() => {
    const groups: Array<{ siteSlug: string; siteName: string; files: CfoSwitcherFile[] }> = []
    for (const file of switcherFiles) {
      const last = groups[groups.length - 1]
      if (last && last.siteSlug === file.siteSlug) {
        last.files.push(file)
      } else {
        groups.push({ siteSlug: file.siteSlug, siteName: file.siteName, files: [file] })
      }
    }
    return groups
  }, [switcherFiles])

  const renameFile = useCallback(async () => {
    if (!wb) return
    setMenuOpen(false)
    try {
      const current = await appApi.getWorkbook(wb)
      const currentName = trimStr(current?.name)
      const next = window.prompt('Rename CFO Core file', currentName)
      const name = trimStr(next)
      if (!name || name === currentName) return
      await appApi.renameWorkbook(wb, name)
      // Force the switcher list to rebuild with the new name on next open.
      setSwitcherStatus('idle')
      await onRenamed?.()
    } catch (err: any) {
      window.alert(formatError(err, 'Could not rename this CFO Core file.'))
    }
  }, [appApi, onRenamed, wb])

  const switchToFile = useCallback((file: CfoSwitcherFile) => {
    setMenuOpen(false)
    if (file.current) return
    const page = (typeof window !== 'undefined' ? parseCfoRouteState(window.location)?.page : null) || 'home'
    const href = buildCfoRoutePath({ siteSlug: file.siteSlug, workbookId: file.workbookId, page })
    if (href && href !== '/' && typeof window !== 'undefined') {
      // A full navigation guarantees the new workbook's bootstrap, worker, and
      // period state load cleanly — switching companies is a hard context change.
      window.location.assign(href)
    }
  }, [])

  const returnToCockpit = useCallback(() => {
    if (typeof window === 'undefined') return
    const siteSlug = currentSiteSlug()
    if (!siteSlug) return
    setMenuOpen(false)
    const target = buildFirmCockpitPath(siteSlug)
    const current = `${window.location.pathname}${window.location.search}${window.location.hash}`
    if (target === current) return
    window.history.pushState(window.history.state, '', target)
    window.dispatchEvent(new Event('fg_route_change'))
  }, [])

  /**
   * Sign out from inside CFO Core. Without this the only sign-out lived on the
   * account-level experiences selector, which a demo session can never reach
   * (it redirects back to the sample firm), so demo visitors had no way to log
   * into their own company. Clearing the tokens also clears the demo marker.
   */
  const signOut = useCallback(async () => {
    setMenuOpen(false)
    try {
      await appApi.logout()
    } catch {
      /* best-effort: the local session is cleared either way */
    }
    appApi.setToken(null)
    if (typeof window !== 'undefined') window.location.assign('/')
  }, [appApi])

  useEffect(() => {
    if (!menuOpen) return
    const onDocumentClick = (event: MouseEvent) => {
      if (!menuRef.current?.contains(event.target as Node)) setMenuOpen(false)
    }
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setMenuOpen(false)
    }
    document.addEventListener('mousedown', onDocumentClick)
    document.addEventListener('keydown', onKeyDown)
    return () => {
      document.removeEventListener('mousedown', onDocumentClick)
      document.removeEventListener('keydown', onKeyDown)
    }
  }, [menuOpen])

  const connection = useMemo(() => resolveConnection(bootstrap, status), [bootstrap, status])
  const connectionId = trimStr(connection?.connection_id || bootstrap?.connection_id || bootstrap?.bindings?.[0]?.connection_id)
  const realmId = trimStr(connection?.realm_id || bootstrap?.realm_id)
  const companyName = trimStr(accountingConnection?.organization_name || accountingBootstrap?.accounting_company_name || connection?.company_name || bootstrap?.company_name) || `${providerName} company`
  const switcherTriggerLabel = trimStr(accountingBootstrap?.accounting_company_name || bootstrap?.company_name || connection?.company_name) || 'CFO Core'
  const otherFilesCount = switcherFiles.filter((file) => !file.current).length
  const workbookName = trimStr(deleteImpact?.workbook_name || companyName) || 'CFO Core'
  const connectionStatus = trimStr(accountingConnection?.status || connection?.status) || (loading ? 'loading' : 'unknown')
  const connected = connectionStatus === 'active'

  const openPanel = useCallback(() => {
    setMenuOpen(false)
    setPanelOpen(true)
    setNotice('')
  }, [])

  const reconnect = useCallback(async () => {
    if (!wb) return
    setBusyAction('reconnect')
    setError('')
    try {
      const out = isQuickBooks
        ? await appApi.createQuickBooksAuthorizeUrl({ realm_id: realmId || null, redirect: currentLocation(), workbook_id: wb })
        : providerKey === 'xero'
          ? await appApi.createXeroAuthorizeUrl(currentLocation() || '/')
          : providerKey === 'sage_accounting'
            ? await appApi.createSageAccountingAuthorizeUrl(currentLocation() || '/')
            : null
      if (!out?.authorize_url) throw new Error(`${providerName} reconnect is not enabled for this tenant.`)
      window.location.href = out.authorize_url
    } catch (err: any) {
      setError(formatError(err, `Could not start the ${providerName} reconnect.`))
      setBusyAction('')
    }
  }, [appApi, isQuickBooks, providerKey, providerName, realmId, wb])

  const disconnect = useCallback(async () => {
    if (isQuickBooks && !realmId) {
      setError('This CFO Core file does not have a resolved QuickBooks company to disconnect.')
      return
    }
    const ok = window.confirm(`Disconnect ${companyName} from CFO Core? FuseGrid will stop ${providerName} refreshes and freeze the last-ready reports.`)
    if (!ok) return
    setBusyAction('disconnect')
    setError('')
    try {
      if (isQuickBooks) await appApi.disconnectQuickBooks({ realm_id: realmId })
      else await appApi.disconnectAccountingWorkbook(wb, true)
      await loadDetails()
      await onConnectionChanged?.()
    } catch (err: any) {
      setError(formatError(err, `Could not disconnect ${providerName}.`))
    } finally {
      setBusyAction('')
    }
  }, [appApi, companyName, isQuickBooks, loadDetails, onConnectionChanged, providerName, realmId, wb])

  const refreshProvider = useCallback(async () => {
    const siteSlug = currentSiteSlug()
    if (!wb || (isQuickBooks && (!siteSlug || !connectionId))) {
      setError(`This CFO Core file does not have a resolved ${providerName} connection to refresh.`)
      return
    }
    setBusyAction('refresh')
    setError('')
    setNotice('')
    try {
      if (isQuickBooks) await appApi.triggerSiteQuickBooksIncrementalRefresh(siteSlug, connectionId)
      else await appApi.refreshAccountingWorkbook(wb)
      setNotice(`${providerName} refresh queued.`)
      await loadDetails()
    } catch (err: any) {
      const { code, status: refusedStatus } = apiErrorBody(err)
      if (code === 'quickbooks_sync_already_active') {
        setError(`A ${providerName} refresh is already running for this company.`)
      } else if (
        (isQuickBooks && code === 'quickbooks_connection_not_active') ||
        (!isQuickBooks && code === 'connection_not_refreshable')
      ) {
        // Reload first so the panel shows the connection's current state
        // beside the connect action. The reload clears the error, so it is
        // set afterwards.
        await loadDetails()
        if (isQuickBooks || RECONNECT_CONNECTION_STATUSES.has(refusedStatus)) {
          setError(`${providerName} needs to be reconnected before this company can refresh.`)
        } else {
          setError(`The ${providerName} connection for this company is in an error state, so it cannot refresh.`)
        }
      } else if (!isQuickBooks && code === 'demo_company_does_not_sync') {
        setError(`This demo company uses fixed sample books, so there is nothing to refresh from ${providerName}.`)
      } else {
        // A typed refusal's server text is the operator contract (`<code>:
        // <detail>`, sometimes with a layout UUID); the reader gets the plain
        // sentence for that code instead. Only for a RECOGNISED refusal,
        // though: `parseCfoRefusal` also returns a tidied-up sentence for
        // anything it does not know, and a rejection that never reached a
        // route (`Failed to fetch`) or an infrastructure failure ("pool timed
        // out while waiting for an open connection") is exactly what
        // `formatError` already has plain copy for. Taking the parsed sentence
        // whenever it is non-empty would make that copy unreachable and put
        // browser and database internals back in front of the reader.
        const refusal = parseCfoRefusal(err)
        setError(refusal.matched
          ? refusal.sentence
          : formatError(err, `Could not start the ${providerName} refresh.`))
      }
    } finally {
      setBusyAction('')
    }
  }, [appApi, connectionId, isQuickBooks, loadDetails, providerName, wb])

  const openDelete = useCallback(async () => {
    if (!wb) return
    setMenuOpen(false)
    setPanelOpen(false)
    setDeleteOpen(true)
    setDeleteImpact(null)
    setDeleteError('')
    setDeleteUnsharedDatasources(false)
    setDeleteImpactLoading(true)
    try {
      const impact = await appApi.getWorkbookDeleteImpact(wb)
      setDeleteImpact(impact)
      setDeleteUnsharedDatasources(Number(impact?.exclusive_deletable_datasource_count || 0) > 0)
    } catch (err: any) {
      setDeleteError(formatError(err, 'Could not load delete impact.'))
    } finally {
      setDeleteImpactLoading(false)
    }
  }, [appApi, wb])

  const closeDelete = useCallback(() => {
    if (busyAction === 'delete') return
    setDeleteOpen(false)
    setDeleteImpact(null)
    setDeleteImpactLoading(false)
    setDeleteError('')
    setDeleteUnsharedDatasources(false)
  }, [busyAction])

  const confirmDelete = useCallback(async () => {
    if (!wb) return
    setBusyAction('delete')
    setDeleteError('')
    try {
      await appApi.deleteWorkbookWithOptions(wb, {
        delete_unshared_datasources: deleteUnsharedDatasources,
      })
      setDeleteOpen(false)
      if (onDeleted) {
        await onDeleted()
      } else {
        navigateToSiteHome()
      }
    } catch (err: any) {
      setDeleteError(formatError(err, 'Could not delete this CFO Core file.'))
      throw err
    } finally {
      setBusyAction('')
    }
  }, [appApi, deleteUnsharedDatasources, onDeleted, wb])

  // Nothing left to offer: the per-row "Manage" menu is only file actions, and a
  // demo session has none. Render no trigger rather than an empty dropdown.
  if (!showSwitcher && !showFileActions) return null

  const statusLabel = connected
    ? 'Connected'
    : connectionStatus === 'revoked'
      ? 'Disconnected'
      : connectionStatus === 'needs_reconnect'
        ? 'Reconnect needed'
        : 'Connection status unknown'

  return (
    <>
      <div className="cfo-file-menu" ref={menuRef}>
        {showSwitcher ? (
          <button
            type="button"
            className="cfo-file-menu-trigger cfo-file-menu-trigger-switcher"
            aria-haspopup="menu"
            aria-expanded={menuOpen}
            title={`${switcherTriggerLabel} — ${showFileActions ? 'switch company or manage file' : 'switch company or sign out'}`}
            onClick={() => setMenuOpen((open) => !open)}
          >
            <Building2 size={16} aria-hidden="true" />
            <span className="cfo-file-menu-trigger-label">{switcherTriggerLabel}</span>
            <ChevronDown size={15} aria-hidden="true" className="cfo-file-menu-trigger-caret" />
          </button>
        ) : (
          <button
            type="button"
            className="cfo-file-menu-trigger"
            aria-haspopup="menu"
            aria-expanded={menuOpen}
            title="CFO Core file actions"
            onClick={() => setMenuOpen((open) => !open)}
          >
            <MoreHorizontal size={17} aria-hidden="true" />
            <span>{triggerLabel}</span>
          </button>
        )}
        {menuOpen ? (
          <div className="cfo-file-menu-list" role="menu" aria-label="CFO Core file actions">
            {showSwitcher ? (
              <>
              <div className="cfo-file-menu-switcher">
                <p className="cfo-file-menu-section">Switch company</p>
                {switcherStatus === 'loading' ? (
                  <div className="cfo-file-menu-switcher-note">
                    <Loader2 size={14} className="spin" aria-hidden="true" />
                    <span>Loading your companies…</span>
                  </div>
                ) : switcherStatus === 'error' ? (
                  <div className="cfo-file-menu-switcher-note">Could not load your other companies.</div>
                ) : switcherFiles.length === 0 ? (
                  <div className="cfo-file-menu-switcher-note">No CFO Core files found.</div>
                ) : (
                  switcherGroups.map((group) => (
                    <div key={group.siteSlug} className="cfo-file-menu-site-group">
                      {switcherGroups.length > 1 ? (
                        <p className="cfo-file-menu-site-name" title={group.siteName}>{group.siteName}</p>
                      ) : null}
                      {group.files.map((file) => (
                        <button
                          key={`${file.siteSlug}/${file.workbookId}`}
                          type="button"
                          role="menuitemradio"
                          aria-checked={file.current}
                          className={`cfo-file-menu-company${file.current ? ' is-current' : ''}`}
                          title={file.label}
                          onClick={() => switchToFile(file)}
                        >
                          <Building2 size={16} aria-hidden="true" />
                          <span className="cfo-file-menu-company-name">{file.label}</span>
                          {file.current ? <Check size={15} aria-hidden="true" className="cfo-file-menu-company-check" /> : null}
                        </button>
                      ))}
                    </div>
                  ))
                )}
                {switcherStatus === 'ready' && otherFilesCount === 0 && switcherFiles.length > 0 ? (
                  <div className="cfo-file-menu-switcher-note">You only have access to this company.</div>
                ) : null}
              </div>
              <div className="cfo-file-menu-divider" role="separator" />
              <button type="button" role="menuitem" onClick={returnToCockpit}>
                <ArrowLeft size={16} aria-hidden="true" />
                <span>Firm cockpit</span>
              </button>
              </>
            ) : null}
            {showFileActions ? (
              <>
                {showSwitcher ? (
                  <>
                    <div className="cfo-file-menu-divider" role="separator" />
                    <p className="cfo-file-menu-section">This file</p>
                  </>
                ) : null}
                <button type="button" role="menuitem" onClick={() => void renameFile()}>
                  <Pencil size={16} aria-hidden="true" />
                  <span>Rename CFO Core file</span>
                </button>
                <button type="button" role="menuitem" onClick={openPanel}>
                  <Building2 size={16} aria-hidden="true" />
                  <span>Manage {providerName}</span>
                </button>
                <button type="button" role="menuitem" onClick={() => void reconnect()} disabled={busyAction === 'reconnect'}>
                  {busyAction === 'reconnect' ? <Loader2 size={16} className="spin" aria-hidden="true" /> : <RefreshCw size={16} aria-hidden="true" />}
                  <span>{connected ? `Reconnect ${providerName}` : `Connect ${providerName}`}</span>
                </button>
                <button type="button" role="menuitem" className="danger" onClick={() => void openDelete()}>
                  <Trash2 size={16} aria-hidden="true" />
                  <span>Delete CFO Core file</span>
                </button>
              </>
            ) : null}
            {showSwitcher ? (
              <>
                <div className="cfo-file-menu-divider" role="separator" />
                <button type="button" role="menuitem" onClick={() => void signOut()}>
                  <LogOut size={16} aria-hidden="true" />
                  <span>{demoSession ? 'Sign out of the demo' : 'Sign out'}</span>
                </button>
              </>
            ) : null}
          </div>
        ) : null}
      </div>

      {panelOpen ? (
        <div className="cfo-file-panel-backdrop" onClick={() => setPanelOpen(false)}>
          <section className="cfo-file-panel" role="dialog" aria-modal="true" aria-label={`${providerName} and CFO Core file settings`} onClick={(event) => event.stopPropagation()}>
            <div className="cfo-file-panel-head">
              <div>
                <p>File settings</p>
                <h2>{companyName}</h2>
              </div>
              <button type="button" onClick={() => setPanelOpen(false)} aria-label="Close file settings">
                <X size={16} aria-hidden="true" />
              </button>
            </div>
            {error ? (
              <div className="cfo-file-alert" role="alert">
                <AlertTriangle size={16} aria-hidden="true" />
                <span>{error}</span>
              </div>
            ) : null}
            {notice ? (
              <div className="cfo-file-alert cfo-file-alert-info" role="status">
                <RefreshCw size={16} aria-hidden="true" />
                <span>{notice}</span>
              </div>
            ) : null}
            <div className="cfo-file-status">
              <div className="cfo-file-status-icon" data-connected={connected ? 'true' : undefined}>
                {connected ? <PlugZap size={20} aria-hidden="true" /> : <Unplug size={20} aria-hidden="true" />}
              </div>
              <div>
                <h3>{providerName}</h3>
                <p>{statusLabel}{isQuickBooks && realmId ? ` · realm ${realmId}` : ''}</p>
              </div>
              {loading ? <Loader2 size={18} className="spin" aria-label={`Loading ${providerName} connection`} /> : null}
            </div>
            <div className="cfo-file-panel-actions">
              <button type="button" onClick={() => void renameFile()}>
                <Pencil size={16} aria-hidden="true" />
                <span>Rename CFO Core file</span>
              </button>
              <button type="button" onClick={() => void refreshProvider()} disabled={!connected || (isQuickBooks && !connectionId) || busyAction === 'refresh'}>
                {busyAction === 'refresh' ? <Loader2 size={16} className="spin" aria-hidden="true" /> : <RefreshCw size={16} aria-hidden="true" />}
                <span>Refresh {providerName}</span>
              </button>
              <button type="button" onClick={() => void reconnect()} disabled={busyAction === 'reconnect'}>
                {busyAction === 'reconnect' ? <Loader2 size={16} className="spin" aria-hidden="true" /> : <ExternalLink size={16} aria-hidden="true" />}
                <span>{connected ? `Reconnect ${providerName}` : `Connect ${providerName}`}</span>
              </button>
              <button type="button" onClick={() => void disconnect()} disabled={!connected || busyAction === 'disconnect'}>
                {busyAction === 'disconnect' ? <Loader2 size={16} className="spin" aria-hidden="true" /> : <Unplug size={16} aria-hidden="true" />}
                <span>Disconnect {providerName}</span>
              </button>
              <button type="button" className="danger" onClick={() => void openDelete()}>
                <FileX2 size={16} aria-hidden="true" />
                <span>Permanently delete company data</span>
              </button>
            </div>
          </section>
        </div>
      ) : null}

      <WorkbookDeleteDialog
        open={deleteOpen}
        workbookName={workbookName}
        accountingCompanyName={companyName}
        accountingProviderName={providerName}
        impact={deleteImpact}
        loading={deleteImpactLoading}
        busy={busyAction === 'delete'}
        error={deleteError || null}
        deleteUnsharedDatasources={deleteUnsharedDatasources}
        onToggleDeleteUnsharedDatasources={setDeleteUnsharedDatasources}
        onCancel={closeDelete}
        onConfirm={confirmDelete}
      />
    </>
  )
}

export default CfoCoreFileMenu
